import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import _ from 'lodash';
import AddUpdateBrochure from '..';
import getBrochureTripId from '../../../../helpers/getBrochureTripId';
import useHandleTourAddToBrochure from '../../../../hooks/useHandleTourAddToBrochure';
import { TravelTypes } from '../../../../constants';

const TourAddUpdateToBrochure = ({ tripId, tourItinerary, className }) => {
  const router = useRouter();
  const { brochure, isViewingBrochure } = useSelector((state) => state.brochure);
  const { staterooms: stateroomsObj } = useSelector((store) => store.tripSearch.searchObj);
  const [isSelected, setIsSelected] = useState(false);
  const isBrochurePage = (router?.pathname || '').indexOf('brochure') > -1;

  const id = useMemo(
    () => getBrochureTripId(tripId, brochure, tourItinerary, stateroomsObj, TravelTypes.tour),
    [brochure, tourItinerary, stateroomsObj, tripId],
  );

  const findTourItinerary = (tripSegments, uniqueKey) =>
    _.find(tripSegments, (ts) => ts.itinerary.uniqueKey === uniqueKey);

  const findTourItineraries = (tripSegments) =>
    _.filter(tripSegments, (ts) => ts.itinerary.mode === TravelTypes.tour);

  const currentBrochure = useSelector((state) => state.brochure.brochures[brochure && brochure.id]);
  const brochureOnBrochurePage = useSelector((state) => state.brochure.brochures[tripId]);

  const setSelectedTripSegment = useCallback(
    (tripSegments) => {
      if (
        tripSegments &&
        tripSegments.length &&
        findTourItinerary(tripSegments, tourItinerary.uniqueKey) != null
      ) {
        setIsSelected(true);
      }
    },
    [tourItinerary.uniqueKey],
  );

  useEffect(() => {
    setIsSelected(false);

    const findAndSetTripSegments = (brochureData, tripIdentifier) => {
      if (!brochureData || !brochureData.trips || brochureData.trips.length === 0) {
        return;
      }

      const isMatchingTourSegment = (segment) =>
        segment?.itinerary?.mode === TravelTypes.tour &&
        segment?.itinerary?.uniqueKey === tourItinerary?.uniqueKey;

      const isMatchingTrip = (trip) =>
        trip.id === tripIdentifier && trip?.tripSegments?.some(isMatchingTourSegment);

      const foundTrip = brochureData.trips.find(isMatchingTrip);

      if (foundTrip && foundTrip.tripSegments) {
        setSelectedTripSegment(foundTrip.tripSegments);
      }
    };

    if (isBrochurePage && tripId) {
      findAndSetTripSegments(brochureOnBrochurePage, tripId);
      return;
    }

    if (isViewingBrochure && currentBrochure && brochure && brochure.id) {
      findAndSetTripSegments(currentBrochure, brochure.id);
      return;
    }

    if (brochure && brochure.trips && brochure.trips[id]) {
      const tripSegments = findTourItineraries(brochure.trips[id].tripSegments);
      setSelectedTripSegment(tripSegments);
    }
  }, [
    brochure,
    brochureOnBrochurePage,
    currentBrochure,
    tourItinerary,
    isBrochurePage,
    isViewingBrochure,
    setSelectedTripSegment,
    tripId,
    id,
  ]);

  const handleAddToBrochureClick = useHandleTourAddToBrochure({
    tripId,
    tourItinerary,
    brochure,
    isViewingBrochure,
    isBrochurePage,
    currentBrochure,
    brochureOnBrochurePage,
  });

  return (
    <AddUpdateBrochure
      handleAddToBrochureClick={handleAddToBrochureClick}
      className={className}
      isSelected={isSelected}
      tripId={tripId}
    />
  );
};

TourAddUpdateToBrochure.propTypes = {
  className: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  tourItinerary: PropTypes.shape().isRequired,
};

TourAddUpdateToBrochure.defaultProps = {
  className: '',
};

export default TourAddUpdateToBrochure;
